import { useNavigate } from "react-router-dom";
import { useState, useCallback, useEffect, useRef } from "react";
import Select, { components } from "react-select";
import axios from "axios";
import MonthPickerModal from "../../components/MonthPickerModal/MonthPickerModal";
import DatePickerModal from "../../components/DatePickerModal/DatePickerModal";
import Generale from "./Generale/Generale";
import Dettaglio from "./Dettaglio/Dettaglio";
import ControlloKmOre from "./ControlloKmOre/ControlloKmOre";
import StatusNoleggi from "./StatusNoleggi/StatusNoleggi";
import FiltroCategorie from "../../components/FiltroCategorie/FiltroCategorie";
import { MOCKED_OPERAZIONI_AUTOMEZZI_CATEGORIES } from "../../mocks/MockedCategories";

import "../OperazioniHr/OperazioniHr.css";

// Componente custom per mostrare numero elementi selezionati
const MultiValueContainer = ({ children, selectProps, data, ...props }) => {
    const values = selectProps.value || [];
    const index = values.findIndex(v => v.value === data.value);

    // Mostra solo i primi 2 elementi + un contatore
    if (index < 2) {
        return <components.MultiValue selectProps={selectProps} data={data} {...props}>{children}</components.MultiValue>;
    }
    if (index === 2 && values.length > 2) {
        return (
            <div className="oper-hr-select-more-badge">
                +{values.length - 2}
            </div>
        );
    }
    return null;
};

// Funzione helper per formattare la data
const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

// Funzione helper per convertire da formato month (yyyy-MM) al primo giorno del mese
const monthToFirstDay = (monthValue) => {
    if (!monthValue) return '';
    const [year, month] = monthValue.split('-');
    return `${year}-${month}-01`;
};

// Funzione helper per convertire da formato month (yyyy-MM) all'ultimo giorno del mese
const monthToLastDay = (monthValue) => {
    if (!monthValue) return '';
    const [year, month] = monthValue.split('-');
    const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
    return `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
};

// Funzione helper per convertire da formato date (yyyy-mm-dd) a formato month (yyyy-MM)
const dateToMonth = (dateValue) => {
    if (!dateValue) return '';
    const [year, month] = dateValue.split('-');
    return `${year}-${month}`;
};

const getDefaultDateRange = () => {
    const today = new Date();
    const firstDay12MonthsAgo = new Date(today.getFullYear(), today.getMonth() - 11, 1);

    return {
        from: formatDate(firstDay12MonthsAgo),
        to: formatDate(today),
    }
};

const defaultDates = getDefaultDateRange();

const OperazioniAutomezzi = () => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
    const navigate = useNavigate();
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showFiltersPanel, setShowFiltersPanel] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [loading, setLoading] = useState(false);

    // cache per evitare reload
    const dataCache = useRef({
        filters: null, 
        data: null
    });

    // Stati per i 7 filtri (tutti multi-select)
    const [fromDate, setFromDate] = useState(defaultDates.from);
    const [toDate, setToDate] = useState(defaultDates.to);
    const [selectedSettore, setSelectedSettore] = useState([]);
    const [selectedCommessa, setSelectedCommessa] = useState([]);
    const [selectedOperatore, setSelectedOperatore] = useState([]);
    const [selectedTipologiaVeicolo, setSelectedTipologiaVeicolo] = useState([]);
    const [selectedSottotipologia, setSelectedSottotipologia] = useState([]);
    const [selectedTarga, setSelectedTarga] = useState([]);
    const [selectedCantiere, setSelectedCantiere] = useState([]);

    // Handler per la selezione del mese di inizio
    const handleFromMonthChange = (e) => {
        const monthValue = e.target.value;
        const firstDay = monthToFirstDay(monthValue);
        setFromDate(firstDay);
    };

    // Handler per la selezione del mese di fine
    const handleToMonthChange = (e) => {
        const monthValue = e.target.value;
        const lastDay = monthToLastDay(monthValue);
        setToDate(lastDay);
    };

    // Filtri effettivamente applicati (passati ai grafici)
    const [appliedFilters, setAppliedFilters] = useState({
        fromDate: defaultDates.from,
        toDate: defaultDates.to,
        settore: [],
        commessa: [],
        operatore: [],
        tipologiaVeicolo: [],
        sottotipologia: [],
        targa: [],
        cantiere: []
    });

    // Opzioni filtri caricate dal backend
    const [filterOptions, setFilterOptions] = useState({
        periodi: [
            { value: 2025, label: "2025" },
            { value: 2024, label: "2024" },
            { value: 2023, label: "2023" }
        ],
        tipologieVeicolo: [],
        sottotipologie: [],
        settori: [],
        commesse: [],
        operatori: [],
        targhe: [],
        cantieri: []
    });

    // Carica le opzioni dei filtri dal backend
    useEffect(() => {
        const fetchFilters = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API_URL}/operation-automezzi/generale/filters`);
                setFilterOptions(prev => ({
                    ...prev,
                    tipologieVeicolo: res.data.tipologie.map(t => ({ value: t.valore, label: t.valore })),
                    sottotipologie: (res.data.sottotipologie || []).map(s => ({ value: s.valore, label: s.valore })),
                    targhe: res.data.targhe.map(t => ({ value: t.valore, label: t.valore })),
                    settori: res.data.settori.map(s => ({ value: s.id, label: s.valore })),
                    commesse: res.data.commesse.map(c => ({ value: c.id, label: c.valore })),
                    operatori: res.data.operatori.map(o => ({ value: o.id, label: o.valore })),
                    cantieri: res.data.cantieri.map(c => ({ value: c.valore, label: c.valore })),
                    attivita: res.data.attivita || []
                }));
            } catch (error) {
                console.error("Errore nel caricamento dei filtri:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchFilters();
    }, [API_URL]);

    // Handler toggle per pannello filtri
    const handlePanelToggle = useCallback(() => {
        setShowFiltersPanel(prev => !prev);
    }, []);

    // Handler per applicare i filtri ai grafici
    const handleApply = useCallback(() => {
        setAppliedFilters({
            fromDate: fromDate,
            toDate: toDate,
            settore: selectedSettore,
            commessa: selectedCommessa,
            operatore: selectedOperatore,
            tipologiaVeicolo: selectedTipologiaVeicolo,
            sottotipologia: selectedSottotipologia,
            targa: selectedTarga,
            cantiere: selectedCantiere
        });
    }, [fromDate, toDate, selectedSettore, selectedCommessa, selectedOperatore, selectedTipologiaVeicolo, selectedSottotipologia, selectedTarga, selectedCantiere]);

    // Handler per reset filtri
    const handleReset = useCallback(() => {
        setFromDate(defaultDates.from);
        setToDate(defaultDates.to);
        setSelectedSettore([]);
        setSelectedCommessa([]);
        setSelectedOperatore([]);
        setSelectedTipologiaVeicolo([]);
        setSelectedSottotipologia([]);
        setSelectedTarga([]);
        setSelectedCantiere([]);
        setAppliedFilters({
            fromDate: defaultDates.from,
            toDate: defaultDates.to,
            settore: [],
            commessa: [],
            operatore: [],
            tipologiaVeicolo: [],
            sottotipologia: [],
            targa: [],
            cantiere: []
        });
    }, []);

    return (
        <div className="operazioni-hr-page">
            <header className="oper-hr-header">
                <div className="oper-hr-header-content">
                    <button className="oper-hr-back-btn" onClick={() => navigate('/reportistica')}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                        Indietro
                    </button>

                    <div className="oper-hr-header-title">
                        <div className="oper-hr-title-row">
                            <h1>Operations Automezzi</h1>
                            <button className="oper-hr-info-btn" onClick={() => setShowInfoModal(true)} title="Informazioni">
                                <span>i</span>
                            </button>
                        </div>
                        <p className="oper-hr-subtitle">Visualizzazione dei mezzi</p>
                    </div>
                </div>
            </header>

            {/* Modal Info */}
            {showInfoModal && (
                <div className="oper-hr-modal-overlay" onClick={() => setShowInfoModal(false)}>
                    <div className="oper-hr-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="oper-hr-modal-header">
                            <div className="oper-hr-modal-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="16" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                            </div>
                            <h3>Informazioni sulla pagina</h3>
                            <button className="oper-hr-modal-close" onClick={() => setShowInfoModal(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="oper-hr-modal-body">
                            <div className="oper-hr-modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Questa pagina permette di analizzare i dati operativi della flotta automezzi.
                                    Puoi monitorare chilometri percorsi, ore di utilizzo, stato dei noleggi
                                    e analizzare i dati per tipologia veicolo, sottotipologia e targa.
                                </p>
                            </div>
                            <div className="oper-hr-modal-section">
                                <h4>Come utilizzarla?</h4>
                                <ul>
                                    <li><strong>Filtri:</strong> Applica filtri per periodo, tipologia veicolo, sottotipologia e targa</li>
                                    <li><strong>Generale:</strong> Visualizza una panoramica generale dei dati della flotta con targhe circolanti, mensili e annuali</li>
                                    <li><strong>Dettaglio:</strong> Analizza il costo al km per targa e per settore, e la ripartizione dei costi per causale</li>
                                    <li><strong>Controllo Km-Ore:</strong> Monitora e confronta chilometri e ore di utilizzo per singolo veicolo</li>
                                    <li><strong>Status Noleggi:</strong> Analizza lo stato dei noleggi con KPI, grafici e tabelle dettagliate per km e ore, incluso il confronto tra km fatti e km contratto</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <main className="oper-hr-main">

                {/* Bottone Mostra Filtri */}
                <div className="oper-hr-actions">
                    <button
                        type="button"
                        className={`oper-hr-action-btn ${showFiltersPanel ? 'active' : ''}`}
                        onClick={handlePanelToggle}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                        </svg>
                        <span>{showFiltersPanel ? 'Nascondi Filtri' : 'Mostra Filtri'}</span>
                    </button>
                </div>

                {/* Pannello Filtri */}
                {showFiltersPanel && (
                    <div className="oper-hr-panel">
                        <div className="oper-hr-filters-grid">

                            {/* Filtri Periodo: DatePicker per Status Noleggi, MonthPicker per gli altri */}
                            {selectedCategory === "Status Noleggi" ? (
                                <>
                                    <div className="oper-hr-filter-item">
                                        <label>Data Da</label>
                                        <DatePickerModal
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                            max={toDate}
                                            label="Data Da"
                                        />
                                    </div>
                                    <div className="oper-hr-filter-item">
                                        <label>Data A</label>
                                        <DatePickerModal
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                            min={fromDate}
                                            label="Data A"
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="oper-hr-filter-item">
                                        <label>Mese Inizio</label>
                                        <MonthPickerModal
                                            value={dateToMonth(fromDate)}
                                            onChange={handleFromMonthChange}
                                            max={dateToMonth(toDate) || undefined}
                                            label="Mese Inizio"
                                        />
                                    </div>
                                    <div className="oper-hr-filter-item">
                                        <label>Mese Fine</label>
                                        <MonthPickerModal
                                            value={dateToMonth(toDate)}
                                            onChange={handleToMonthChange}
                                            min={dateToMonth(fromDate) || undefined}
                                            label="Mese Fine"
                                        />
                                    </div>
                                </>
                            )}

                            {/* Filtri specifici per Status Noleggi */}
                            {selectedCategory === "Status Noleggi" ? (
                                <>
                                    <div className="oper-hr-filter-item">
                                        <label>Tipologia</label>
                                        <Select
                                            classNamePrefix="oper-hr-select"
                                            placeholder="Tutte"
                                            value={selectedTipologiaVeicolo}
                                            options={filterOptions.tipologieVeicolo}
                                            onChange={setSelectedTipologiaVeicolo}
                                            isMulti
                                            components={{ MultiValue: MultiValueContainer }}
                                            hideSelectedOptions={false}
                                            closeMenuOnSelect={false}
                                        />
                                    </div>
                                    <div className="oper-hr-filter-item">
                                        <label>Sottotipologia</label>
                                        <Select
                                            classNamePrefix="oper-hr-select"
                                            placeholder="Tutte"
                                            value={selectedSottotipologia}
                                            options={filterOptions.sottotipologie}
                                            onChange={setSelectedSottotipologia}
                                            isMulti
                                            components={{ MultiValue: MultiValueContainer }}
                                            hideSelectedOptions={false}
                                            closeMenuOnSelect={false}
                                        />
                                    </div>
                                    <div className="oper-hr-filter-item">
                                        <label>Targa</label>
                                        <Select
                                            classNamePrefix="oper-hr-select"
                                            placeholder="Tutte"
                                            value={selectedTarga}
                                            options={filterOptions.targhe}
                                            onChange={setSelectedTarga}
                                            isMulti
                                            components={{ MultiValue: MultiValueContainer }}
                                            hideSelectedOptions={false}
                                            closeMenuOnSelect={false}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Filtro Settore */}
                                    <div className="oper-hr-filter-item">
                                        <label>Settore</label>
                                        <Select
                                            classNamePrefix="oper-hr-select"
                                            placeholder="Tutte"
                                            value={selectedSettore}
                                            options={filterOptions.settori}
                                            onChange={setSelectedSettore}
                                            isMulti
                                            components={{ MultiValue: MultiValueContainer }}
                                            hideSelectedOptions={false}
                                            closeMenuOnSelect={false}
                                        />
                                    </div>

                                    {/* Filtro Commessa */}
                                    <div className="oper-hr-filter-item">
                                        <label>Commessa</label>
                                        <Select
                                            classNamePrefix="oper-hr-select"
                                            placeholder="Tutte"
                                            value={selectedCommessa}
                                            options={filterOptions.commesse}
                                            onChange={setSelectedCommessa}
                                            isMulti
                                            components={{ MultiValue: MultiValueContainer }}
                                            hideSelectedOptions={false}
                                            closeMenuOnSelect={false}
                                        />
                                    </div>

                                    {/* Filtro Operatore */}
                                    <div className="oper-hr-filter-item">
                                        <label>Operatore</label>
                                        <Select
                                            classNamePrefix="oper-hr-select"
                                            placeholder="Tutte"
                                            value={selectedOperatore}
                                            options={filterOptions.operatori}
                                            onChange={setSelectedOperatore}
                                            isMulti
                                            components={{ MultiValue: MultiValueContainer }}
                                            hideSelectedOptions={false}
                                            closeMenuOnSelect={false}
                                        />
                                    </div>

                                    {/* Filtro Tipologia Veicolo */}
                                    <div className="oper-hr-filter-item">
                                        <label>Tipologia Veicolo</label>
                                        <Select
                                            classNamePrefix="oper-hr-select"
                                            placeholder="Tutte"
                                            value={selectedTipologiaVeicolo}
                                            options={filterOptions.tipologieVeicolo}
                                            onChange={setSelectedTipologiaVeicolo}
                                            isMulti
                                            components={{ MultiValue: MultiValueContainer }}
                                            hideSelectedOptions={false}
                                            closeMenuOnSelect={false}
                                        />
                                    </div>

                                    {/* Filtro Targa */}
                                    <div className="oper-hr-filter-item">
                                        <label>Targa</label>
                                        <Select
                                            classNamePrefix="oper-hr-select"
                                            placeholder="Tutte"
                                            value={selectedTarga}
                                            options={filterOptions.targhe}
                                            onChange={setSelectedTarga}
                                            isMulti
                                            components={{ MultiValue: MultiValueContainer }}
                                            hideSelectedOptions={false}
                                            closeMenuOnSelect={false}
                                        />
                                    </div>

                                    {/* Filtro Cantiere */}
                                    <div className="oper-hr-filter-item">
                                        <label>Cantiere</label>
                                        <Select
                                            classNamePrefix="oper-hr-select"
                                            placeholder="Tutte"
                                            value={selectedCantiere}
                                            options={filterOptions.cantieri}
                                            onChange={setSelectedCantiere}
                                            isMulti
                                            components={{ MultiValue: MultiValueContainer }}
                                            hideSelectedOptions={false}
                                            closeMenuOnSelect={false}
                                        />
                                    </div>
                                </>
                            )}

                            {/* Bottoni Applica e Reset */}
                            <div className="oper-hr-filter-actions">
                                <button type="button" className="oper-hr-btn-primary" onClick={handleApply}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    Applica
                                </button>
                                <button type="button" className="oper-hr-btn-secondary" onClick={handleReset}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                        <path d="M3 3v5h5"></path>
                                    </svg>
                                    Reset
                                </button>
                            </div>

                        </div>
                    </div>
                )}

                <div className="oper-hr-data-section">
                    <FiltroCategorie
                        categories={MOCKED_OPERAZIONI_AUTOMEZZI_CATEGORIES}
                        filteredCategory={setSelectedCategory}
                        customCss={"reportistica-section"}
                    />
                    {selectedCategory === "Generale" &&
                        <Generale filters={appliedFilters} loading={loading} />
                    }
                    {selectedCategory === "Dettaglio" &&
                        <Dettaglio filters={appliedFilters} loading={loading} />
                    }
                    {selectedCategory === "Controllo Km-Ore" &&
                        <ControlloKmOre filters={appliedFilters} loading={loading} filterOptions={filterOptions} />
                    }
                    {selectedCategory === "Status Noleggi" &&
                        <StatusNoleggi filters={appliedFilters} loading={loading} filterOptions={filterOptions} />
                    }
                </div>
            </main>
        </div>
    );
};

export default OperazioniAutomezzi;
